import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { focusParent } from '../ProjectPage/team';

// Données du kickoff et ce qu'on y fait : objectifs (placer dans un sprint, estimer, confier), propositions de Nexus
// (tokens estimés par Kev, titres relus : règle + Kev, reformulation par Muse). Chaque changement prévient les autres
// participants (notify) ; leurs changements rechargent les objectifs (refreshToken).

const list = (data) => (Array.isArray(data) ? data : data?.items || []);
const byStart = (a, b) => String(a.start_date || '').localeCompare(String(b.start_date || ''));

export default function useKickoffData(projectId, { refreshToken, notify }) {
  const [project, setProject] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [circles, setCircles] = useState([]);
  const [focus, setFocus] = useState([]);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState({}); // id -> { suggestion, by } ; 'pending' pendant la relecture
  const [estimates, setEstimates] = useState({}); // id -> réponse de /assist/estimate ; 'pending'
  const objectivesRef = useRef(objectives);
  objectivesRef.current = objectives;
  const errorTimer = useRef(null);
  const estimateRef = useRef(null);

  const fail = useCallback((message) => {
    setError(message);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(null), 6000);
  }, []);
  useEffect(() => () => clearTimeout(errorTimer.current), []);

  const loadObjectives = useCallback(async () => {
    try {
      setObjectives(list(await apiService.get(`/projects/${projectId}/objectives`)));
    } catch (err) {
      fail(`Objectifs : ${err?.message || 'chargement impossible'}`);
    }
  }, [projectId, fail]);

  const loadIterations = useCallback(async () => {
    const data = list(await apiService.get(`projects/${projectId}/iterations`).catch(() => []));
    setIterations(data.slice().sort(byStart).map((it) => ({ ...it, sprints: (it.sprints || []).slice().sort(byStart) })));
  }, [projectId]);

  useEffect(() => {
    apiService.get(`/projects/${projectId}`).then(setProject).catch((err) => fail(err?.message || 'Projet introuvable'));
    apiService.get('/employees').then((d) => setEmployees(list(d))).catch(() => setEmployees([]));
    apiService.get('/circles').then((d) => setCircles(list(d))).catch(() => setCircles([]));
    loadIterations();
  }, [projectId, loadIterations, fail]);

  useEffect(() => { loadObjectives(); }, [loadObjectives, refreshToken]);
  useEffect(() => { if (refreshToken) loadIterations(); }, [refreshToken, loadIterations]);

  const parent = focusParent(project, circles);
  useEffect(() => {
    if (parent == null) { setFocus([]); return; }
    apiService.get(`circle-focus?parent_id=${parent}`).then((d) => setFocus(list(d))).catch(() => setFocus([]));
  }, [parent]);

  const patch = useCallback(async (objective, fields) => {
    const before = objectivesRef.current.find((o) => o.id === objective.id);
    setObjectives((items) => items.map((o) => (o.id === objective.id ? { ...o, ...fields } : o)));
    try {
      const saved = await apiService.patch(`/projects/${projectId}/objectives/${objective.id}`, fields);
      setObjectives((items) => items.map((o) => (o.id === objective.id ? { ...o, ...saved } : o)));
      notify('objectives');
      if (fields.name && !saved.effort) estimateRef.current?.(saved); // titre changé : tokens revus (stables si proches)
      return saved;
    } catch (err) {
      setObjectives((items) => items.map((o) => (o.id === objective.id ? before : o)));
      fail(`Mise à jour impossible : ${err?.message || 'erreur'}`);
      return null;
    }
  }, [projectId, notify, fail]);

  const remove = useCallback(async (objective) => {
    setObjectives((items) => items.filter((o) => o.id !== objective.id));
    try {
      await apiService.delete(`/projects/${projectId}/objectives/${objective.id}`);
      notify('objectives');
    } catch (err) {
      fail(`Suppression impossible : ${err?.message || 'erreur'}`);
      loadObjectives();
    }
  }, [projectId, notify, fail, loadObjectives]);

  // --- Nexus : relecture du titre (règle + Kev ; Muse propose une reformulation si besoin).
  const review = useCallback(async (objective) => {
    setReviews((r) => ({ ...r, [objective.id]: 'pending' }));
    try {
      const result = await apiService.post(`/projects/${projectId}/objectives/assist/review`, { name: objective.name });
      setReviews((r) => ({ ...r, [objective.id]: result?.suggestion ? result : null }));
      return result;
    } catch {
      setReviews((r) => ({ ...r, [objective.id]: null }));
      return null;
    }
  }, [projectId]);

  // --- Nexus : tokens proposés (Kev + objectifs proches), gardés sur l'objectif.
  const estimate = useCallback(async (objective) => {
    setEstimates((e) => ({ ...e, [objective.id]: 'pending' }));
    try {
      const result = await apiService.post(`/projects/${projectId}/objectives/assist/estimate`, { objective_id: objective.id });
      setEstimates((e) => ({ ...e, [objective.id]: result?.tokens ? result : null }));
      if (result?.tokens) {
        setObjectives((items) => items.map((o) => (o.id === objective.id ? { ...o, predicted_tokens: result.tokens } : o)));
      }
      return result;
    } catch {
      setEstimates((e) => ({ ...e, [objective.id]: null }));
      return null;
    }
  }, [projectId]);

  estimateRef.current = estimate;

  const create = useCallback(async (name, fields = {}) => {
    try {
      const created = await apiService.post(`/projects/${projectId}/objectives`, {
        project_id: projectId, name, description: '', sprint_id: null, effort: 0, owner_id: null, status: 'not_started',
        blocked_by: [], comment: null, x: null, y: null, ...fields,
      });
      setObjectives((items) => [...items, created]);
      notify('objectives');
      // La relecture d'abord : l'estimation reprend la même reformulation (proposition stable si on l'accepte).
      review(created).then(() => { if (!created.effort) estimate(created); });
      return created;
    } catch (err) {
      fail(`Création impossible : ${err?.message || 'erreur'}`);
      return null;
    }
  }, [projectId, notify, fail, review, estimate]);

  const acceptSuggestion = useCallback(async (objective) => {
    const proposal = reviews[objective.id];
    if (!proposal?.suggestion) return;
    setReviews((r) => ({ ...r, [objective.id]: null }));
    await patch(objective, { name: proposal.suggestion }); // tokens inchangés : déjà estimés sous cette forme
  }, [reviews, patch]);

  const dismissSuggestion = useCallback((objective) => setReviews((r) => ({ ...r, [objective.id]: null })), []);

  // Plusieurs objectifs à la suite, deux à la fois (Kev répond en moins d'une seconde ; Muse est plus lente).
  const runAll = useCallback(async (items, action, onProgress) => {
    let done = 0;
    const queue = items.slice();
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        await action(item); // eslint-disable-line no-await-in-loop
        done += 1;
        onProgress?.(done, items.length);
      }
    };
    await Promise.all([worker(), worker()]);
  }, []);

  return {
    project, objectives, iterations, employees, circles, focus, error, reviews, estimates,
    clearError: () => setError(null), reload: loadObjectives,
    actions: { patch, remove, create, review, estimate, acceptSuggestion, dismissSuggestion, runAll },
  };
}
